import { format } from 'date-fns';
import { Bug, ChevronDown, ChevronUp, RefreshCw, X } from 'lucide-react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
} from '@/components/ui';

/**
 * Error Message Context
 */
const ErrorMessageContext = createContext();

/**
 * Error Message Provider Component
 */
export const ErrorMessageProvider = ({ children }) => {
  const [errors, setErrors] = useState([]);

  const addError = useCallback(error => {
    const normalizedError = normalizeError(error);
    setErrors(prev => [normalizedError, ...prev.slice(0, 4)]); // Keep max 5 errors
    return normalizedError.id;
  }, []);

  const removeError = useCallback(id => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const value = React.useMemo(
    () => ({
      errors,
      addError,
      removeError,
      clearAllErrors,
    }),
    [errors, addError, removeError, clearAllErrors]
  );

  return (
    <ErrorMessageContext.Provider value={value}>
      {children}
      <ErrorMessageDisplay />
    </ErrorMessageContext.Provider>
  );
};

/**
 * Hook to use error messages
 */
export const useErrorMessages = () => {
  const context = useContext(ErrorMessageContext);
  if (!context) {
    throw new Error(
      'useErrorMessages must be used within ErrorMessageProvider'
    );
  }
  return context;
};

/**
 * Normalize error objects to consistent format
 */
const normalizeError = error => {
  const id = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const timestamp = new Date();

  // Handle different error formats
  if (typeof error === 'string') {
    return {
      id,
      timestamp,
      title: 'Error',
      message: error,
      severity: 'error',
      category: 'general',
      retryable: false,
      details: null,
    };
  }

  // API Error format
  if (error.response) {
    const { status, data } = error.response;
    return {
      id,
      timestamp,
      title: getErrorTitle(status, error),
      message: getUserFriendlyMessage(status, data, error),
      severity: getErrorSeverity(status),
      category: getErrorCategory(status, error),
      retryable: isRetryable(status),
      details: {
        status,
        statusText: error.response.statusText,
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        code: data?.code || `HTTP_${status}`,
        originalMessage: error.message,
        responseData: data,
      },
    };
  }

  // Network Error
  if (error.request) {
    return {
      id,
      timestamp,
      title: 'Connection Error',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
      severity: 'error',
      category: 'network',
      retryable: true,
      details: {
        originalMessage: error.message,
        code: 'NETWORK_ERROR',
      },
    };
  }

  // Generic Error
  return {
    id,
    timestamp,
    title: error.name || 'Error',
    message: error.message || 'An unexpected error occurred',
    severity: 'error',
    category: 'application',
    retryable: false,
    details: {
      originalMessage: error.message,
      stack: error.stack,
    },
  };
};

/**
 * Get user-friendly error title
 */
const getErrorTitle = (status, error) => {
  switch (status) {
    case 400:
      return 'Invalid Request';
    case 401:
      return 'Authentication Required';
    case 403:
      return 'Access Denied';
    case 404:
      return 'Not Found';
    case 429:
      return 'Too Many Requests';
    case 500:
      return 'Server Error';
    case 502:
      return 'Service Unavailable';
    case 503:
      return 'Service Unavailable';
    case 504:
      return 'Request Timeout';
    default:
      return error.name || 'Error';
  }
};

/**
 * Get user-friendly error message
 */
const getUserFriendlyMessage = (status, data, error) => {
  // Use server-provided message if available and user-friendly
  if (data?.message && isUserFriendlyMessage(data.message)) {
    return data.message;
  }

  // Generate user-friendly messages based on status
  switch (status) {
    case 400:
      return 'The request contains invalid information. Please check your input and try again.';
    case 401:
      return 'You need to log in to access this feature. Please sign in and try again.';
    case 403:
      return "You don't have permission to perform this action. Please contact an administrator if you believe this is an error.";
    case 404:
      return 'The requested resource could not be found. It may have been moved or deleted.';
    case 429:
      return "You've made too many requests. Please wait a moment before trying again.";
    case 500:
      return 'The server encountered an error while processing your request. Please try again later.';
    case 502:
    case 503:
      return 'The service is temporarily unavailable. Please try again in a few minutes.';
    case 504:
      return 'The request took too long to complete. Please try again.';
    default:
      return (
        data?.message ||
        error.message ||
        'An unexpected error occurred. Please try again.'
      );
  }
};

/**
 * Check if message is user-friendly (not technical)
 */
const isUserFriendlyMessage = message => {
  const technicalTerms = [
    'null pointer',
    'undefined',
    'stack trace',
    'exception',
    'internal server error',
    'sql',
    'database',
    'connection refused',
    'timeout',
    'cors',
    'csrf',
    'jwt',
    'token',
  ];

  const lowerMessage = message.toLowerCase();
  return !technicalTerms.some(term => lowerMessage.includes(term));
};

/**
 * Get error severity
 */
const getErrorSeverity = status => {
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  return 'info';
};

/**
 * Get error category
 */
const getErrorCategory = (status, error) => {
  if (status === 401 || status === 403) return 'auth';
  if (status === 404) return 'notfound';
  if (status === 429) return 'ratelimit';
  if (status >= 500) return 'server';
  if (error.request && !error.response) return 'network';
  return 'client';
};

/**
 * Check if error is retryable
 */
const isRetryable = status => {
  return status >= 500 || status === 429 || status === 408;
};

/**
 * Error Message Display Component
 */
const ErrorMessageDisplay = () => {
  const { errors, removeError } = useErrorMessages();
  const [expandedErrors, setExpandedErrors] = useState(new Set());

  const autoHideTimersRef = useRef(new Map());

  useEffect(() => {
    // Auto-hide only non-error severities (keeps parity with prior Snackbar behavior)
    errors.forEach(error => {
      if (error.severity === 'error') return;
      if (autoHideTimersRef.current.has(error.id)) return;

      const timer = setTimeout(() => {
        removeError(error.id);
        autoHideTimersRef.current.delete(error.id);
      }, 6000);

      autoHideTimersRef.current.set(error.id, timer);
    });

    // Cleanup timers for removed errors
    const activeIds = new Set(errors.map(e => e.id));
    Array.from(autoHideTimersRef.current.keys()).forEach(id => {
      if (!activeIds.has(id)) {
        clearTimeout(autoHideTimersRef.current.get(id));
        autoHideTimersRef.current.delete(id);
      }
    });

    return () => {
      // no-op; full cleanup is handled on unmount below
    };
  }, [errors, removeError]);

  useEffect(() => {
    return () => {
      autoHideTimersRef.current.forEach(timer => clearTimeout(timer));
      autoHideTimersRef.current.clear();
    };
  }, []);

  const toggleExpanded = errorId => {
    setExpandedErrors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(errorId)) {
        newSet.delete(errorId);
      } else {
        newSet.add(errorId);
      }
      return newSet;
    });
  };

  const handleRetry = error => {
    // Emit retry event
    window.dispatchEvent(
      new CustomEvent('error:retry', {
        detail: { error },
      })
    );
    removeError(error.id);
  };

  const handleReport = error => {
    // Emit report event
    window.dispatchEvent(
      new CustomEvent('error:report', {
        detail: { error },
      })
    );
  };

  const getSeverityClasses = severity => {
    switch (severity) {
      case 'error':
        return 'border-red-200 bg-red-50 text-red-900';
      case 'warning':
        return 'border-amber-200 bg-amber-50 text-amber-900';
      case 'info':
      default:
        return 'border-sky-200 bg-sky-50 text-sky-900';
    }
  };

  return (
    <div className='fixed right-4 top-20 z-[1400] w-full max-w-[400px] space-y-2'>
      {errors.map(error => {
        const isExpanded = expandedErrors.has(error.id);

        const hasDetails = !!error.details;
        const timestampText = format(error.timestamp, 'HH:mm:ss');

        return (
          <Alert
            key={error.id}
            className={`relative w-full ${getSeverityClasses(error.severity)}`}
          >
            <div className='flex items-start justify-between gap-3'>
              <div className='min-w-0 flex-1'>
                <AlertTitle>
                  <div className='flex items-center gap-2'>
                    <span className='truncate'>{error.title}</span>
                    <Badge variant='outline' className='shrink-0'>
                      {error.category}
                    </Badge>
                  </div>
                </AlertTitle>
                <AlertDescription className='mt-1'>
                  {error.message}
                </AlertDescription>
              </div>

              <div className='flex shrink-0 items-center gap-1'>
                {hasDetails && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => toggleExpanded(error.id)}
                    className='h-8 w-8'
                    aria-label={
                      isExpanded ? 'Collapse details' : 'Expand details'
                    }
                  >
                    {isExpanded ? (
                      <ChevronUp className='h-4 w-4' />
                    ) : (
                      <ChevronDown className='h-4 w-4' />
                    )}
                  </Button>
                )}
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  onClick={() => removeError(error.id)}
                  className='h-8 w-8'
                  aria-label='Dismiss'
                >
                  <X className='h-4 w-4' />
                </Button>
              </div>
            </div>

            <div className='mt-3 flex flex-wrap items-center gap-2'>
              {error.retryable && (
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => handleRetry(error)}
                >
                  <RefreshCw className='h-4 w-4' />
                  ลองใหม่
                </Button>
              )}
              <Button
                size='sm'
                variant='outline'
                onClick={() => handleReport(error)}
              >
                <Bug className='h-4 w-4' />
                รายงาน
              </Button>
              <span className='ml-auto text-xs text-muted-foreground'>
                {timestampText}
              </span>
            </div>

            {isExpanded && hasDetails && (
              <div className='mt-3 rounded-md bg-background/60 p-3 text-xs text-muted-foreground'>
                <div className='mb-2 font-medium text-foreground'>
                  Technical Details:
                </div>
                {error.details.status && (
                  <div>
                    Status: {error.details.status} {error.details.statusText}
                  </div>
                )}
                {error.details.code && <div>Code: {error.details.code}</div>}
                {error.details.url && (
                  <div>
                    URL: {error.details.method} {error.details.url}
                  </div>
                )}
                {error.details.originalMessage && (
                  <div className='mt-2'>
                    Original: {error.details.originalMessage}
                  </div>
                )}
              </div>
            )}
          </Alert>
        );
      })}
    </div>
  );
};

/**
 * Higher-order component to add error handling
 */
export const withErrorHandling = Component => {
  return function ErrorHandledComponent(props) {
    const { addError } = useErrorMessages();

    return <Component {...props} onError={addError} />;
  };
};

/**
 * Hook for API error handling
 */
export const useApiErrorHandler = () => {
  const { addError } = useErrorMessages();

  const handleError = useCallback(
    error => {
      const errorId = addError(error);
      return errorId;
    },
    [addError]
  );

  return { handleError };
};

export default ErrorMessageProvider;
